/**
 * Form fields.
 *
 * From the Figma contact form (`🛠️ Components / Kontakt`, nodes `701:4006` onwards):
 * a 12px SemiBold label, a 9.6px gap, and a 60px control with a `grey-300` border,
 * `Radius/xs` and 20px of horizontal padding.
 *
 * Three things the file does not settle, resolved here and worth raising:
 *
 * 1. **Two label styles.** The text inputs (`701:4010`) use 18px Regular; the newer
 *    department select (`810:5884`) uses 12px SemiBold. The newer one is used for every
 *    field, because a form with two label sizes is a mistake rather than a decision.
 * 2. **The select is filled `green-50`** (`815:5917`) while every text input is white
 *    with a grey border. That reads as a leftover experiment, so all controls are white.
 * 3. **No focus, error or disabled states are designed** for inputs - only for buttons.
 *    Focus reuses the button's `grey-600` ring so the form is at least consistent with
 *    itself; error uses red, which appears nowhere in the palette. Both need a designer.
 *
 * The wider open question stands: per README.md the contact form's field set follows
 * the project specification, not this mock-up.
 */
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { Icon } from './Icon';

const CONTROL =
  'w-full rounded-xs border border-grey-300 bg-surface px-5 text-body text-ink ' +
  'placeholder:text-grey-700 transition-colors ' +
  'focus:outline-none focus-visible:border-blue-dark-400 focus-visible:ring-[3px] focus-visible:ring-grey-600 ' +
  'disabled:opacity-20 aria-[invalid=true]:border-red-600';

const CONTROL_HEIGHT = 'h-[60px]';

export function Label({ htmlFor, required, children }: { htmlFor: string; required?: boolean; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-caption font-semibold text-ink-strong">
      {children}
      {required ? <span aria-hidden> *</span> : null}
    </label>
  );
}

/** Label, control and error message as one block. */
export function Field({
  id, label, required, error, children
}: { id: string; label: string; required?: boolean; error?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5">
      <Label htmlFor={id} required={required}>{label}</Label>
      {children}
      {error ? <p id={`${id}-error`} className="text-caption text-red-600">{error}</p> : null}
    </div>
  );
}

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${CONTROL_HEIGHT} ${className ?? ''}`} />;
}

export function TextArea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${CONTROL} py-5 ${className ?? ''}`} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select {...props} className={`${CONTROL} ${CONTROL_HEIGHT} appearance-none pr-12 ${className ?? ''}`}>
        {children}
      </select>
      <Icon name="chevron-down" size={14} className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-ink-strong" />
    </div>
  );
}

export function Checkbox({ id, label, error, ...props }: { id: string; label: ReactNode; error?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <div className="flex items-start gap-3">
        <input
          {...props}
          id={id}
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 rounded-xs border border-grey-300 accent-blue-dark-200 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-grey-600"
        />
        <label htmlFor={id} className="text-body-sm text-ink-muted">{label}</label>
      </div>
      {error ? <p className="mt-1 text-caption text-red-600">{error}</p> : null}
    </div>
  );
}
